import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft,
  Utensils,
  Flame,
  Beef,
  Wheat,
  Droplet,
  Clock,
  Check,
  Sparkles,
  ChefHat
} from 'lucide-react';

export default function MealPlanDetailPage() {
  const { planId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedMeals, setCheckedMeals] = useState({});

  const fetchPlan = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/meal-plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlan(response.data);
    } catch (error) {
      toast.error('Meal plan not found');
      navigate('/nutrition');
    } finally {
      setLoading(false);
    }
  }, [planId, token, navigate]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const toggleMealCheck = (mealIndex, foodIndex) => {
    const key = `${mealIndex}-${foodIndex}`;
    setCheckedMeals(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getGoalColor = (goal) => {
    switch (goal) {
      case 'weight_loss': return 'text-orange-500 bg-orange-500/10';
      case 'muscle_gain': return 'text-blue-500 bg-blue-500/10';
      case 'maintenance': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getGoalLabel = (goal) => {
    switch (goal) {
      case 'weight_loss': return 'Weight Loss';
      case 'muscle_gain': return 'Muscle Gain';
      case 'maintenance': return 'Maintenance';
      default: return goal;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!plan) return null;

  return (
    <div data-testid="meal-plan-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="outline" 
          size="icon"
          onClick={() => navigate('/nutrition')}
          className="border-border hover:border-primary"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase">
              {plan.title}
            </h1>
            {plan.is_ai_generated && (
              <span className="text-sm text-primary flex items-center gap-1">
                <Sparkles className="w-4 h-4" />
                AI
              </span>
            )}
          </div>
          <span className={`px-2 py-1 rounded-sm text-xs uppercase font-semibold ${getGoalColor(plan.goal)}`}>
            {getGoalLabel(plan.goal)}
          </span>
        </div>
      </div>

      {/* Macro Overview */}
      <div className="card-tactical">
        <h3 className="font-heading text-lg text-white uppercase mb-4">Daily Targets</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-secondary/50 rounded-sm">
            <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{plan.calories_target}</div>
            <div className="text-xs text-gray-400 uppercase">Calories</div>
          </div>
          <div className="text-center p-4 bg-secondary/50 rounded-sm">
            <Beef className="w-6 h-6 text-red-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{plan.protein_g}g</div>
            <div className="text-xs text-gray-400 uppercase">Protein</div>
          </div>
          <div className="text-center p-4 bg-secondary/50 rounded-sm">
            <Wheat className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{plan.carbs_g}g</div>
            <div className="text-xs text-gray-400 uppercase">Carbs</div>
          </div>
          <div className="text-center p-4 bg-secondary/50 rounded-sm">
            <Droplet className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <div className="font-mono text-2xl text-white">{plan.fats_g}g</div>
            <div className="text-xs text-gray-400 uppercase">Fats</div>
          </div>
        </div>
      </div>

      {/* Description */}
      {plan.description && (
        <div className="card-tactical">
          <p className="text-gray-400">{plan.description}</p>
        </div>
      )}

      {/* Meals */}
      {plan.meals && plan.meals.length > 0 ? (
        <div className="space-y-4">
          <h3 className="font-heading text-xl text-white uppercase">Today's Meals</h3>
          
          {plan.meals.map((meal, mealIndex) => (
            <div key={mealIndex} className="card-tactical">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center">
                    <ChefHat className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg text-white uppercase">
                      {meal.meal_name || meal.name || `Meal ${mealIndex + 1}`}
                    </h4>
                    {meal.time && (
                      <div className="flex items-center gap-1 text-sm text-gray-400">
                        <Clock className="w-3 h-3" />
                        {meal.time}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {meal.foods && meal.foods.length > 0 ? (
                <div className="space-y-2">
                  {meal.foods.map((food, foodIndex) => {
                    const isChecked = checkedMeals[`${mealIndex}-${foodIndex}`];
                    
                    return (
                      <div 
                        key={foodIndex}
                        onClick={() => toggleMealCheck(mealIndex, foodIndex)}
                        className={`flex items-center gap-4 p-3 rounded-sm border cursor-pointer transition-all ${
                          isChecked 
                            ? 'border-green-500 bg-green-500/10' 
                            : 'border-border bg-secondary/30 hover:border-primary/50'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-sm border-2 flex items-center justify-center ${
                          isChecked ? 'bg-green-500 border-green-500' : 'border-gray-600'
                        }`}>
                          {isChecked && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium ${isChecked ? 'text-gray-400 line-through' : 'text-white'}`}>
                            {food.name}
                          </div>
                          {food.portion && (
                            <div className="text-sm text-gray-500">{food.portion}</div>
                          )}
                        </div>
                        <div className="text-right">
                          {food.calories && (
                            <div className="font-mono text-sm text-orange-400">{food.calories} kcal</div>
                          )}
                          <div className="flex gap-2 text-xs text-gray-500">
                            {food.protein && <span>P: {food.protein}g</span>}
                            {food.carbs && <span>C: {food.carbs}g</span>}
                            {food.fats && <span>F: {food.fats}g</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-gray-500 text-sm">No food items specified</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="card-tactical text-center py-8">
          <Utensils className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No detailed meal breakdown available</p>
          <p className="text-sm text-gray-500 mt-1">Follow the macro targets above to plan your meals</p>
        </div>
      )}

      {/* Action Button */}
      <div className="card-tactical">
        <Button 
          data-testid="back-to-nutrition-btn"
          onClick={() => navigate('/nutrition')}
          className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold"
        >
          Back to Nutrition Plans
        </Button>
      </div>
    </div>
  );
}
